import { auth0ApiClient } from './auth0ApiClient'
import type {
  IComment,
  ICreateCommentDTO,
  ICreatePostDTO,
  IPost,
  IUpdatePostDTO
} from '@/types/feed'
import { PostVisibility, ReactionType } from '@/types/feed'

interface PaginatedEnvelope<T> {
  success: boolean
  data: T[]
  pagination: {
    hasMore: boolean
    nextCursor?: string
  }
}

export interface FeedPage<T> {
  items: T[]
  hasMore: boolean
  nextCursor?: string
}

const buildQuery = (params: Record<string, string | number | undefined>): string => {
  const usp = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.append(k, String(v))
  })
  const q = usp.toString()
  return q ? `?${q}` : ''
}

const toFeedPage = <T>(env: PaginatedEnvelope<T>): FeedPage<T> => ({
  items: env.data,
  hasMore: env.pagination.hasMore,
  ...(env.pagination.nextCursor && { nextCursor: env.pagination.nextCursor })
})

class FeedService {
  private readonly baseUrl = '/feed'

  createPost(dto: ICreatePostDTO): Promise<IPost> {
    return auth0ApiClient.post<IPost>(`${this.baseUrl}/posts`, dto)
  }

  updatePost(postId: string, dto: IUpdatePostDTO): Promise<IPost> {
    return auth0ApiClient.patch<IPost>(`${this.baseUrl}/posts/${postId}`, dto)
  }

  deletePost(postId: string): Promise<void> {
    return auth0ApiClient.delete<void>(`${this.baseUrl}/posts/${postId}`)
  }

  getPost(postId: string): Promise<IPost> {
    return auth0ApiClient.get<IPost>(`${this.baseUrl}/posts/${postId}`)
  }

  async getFeed(params: { cursor?: string; limit?: number; visibility?: PostVisibility } = {}): Promise<FeedPage<IPost>> {
    const env = await auth0ApiClient.getEnvelope<PaginatedEnvelope<IPost>>(
      `${this.baseUrl}/posts${buildQuery({ ...params })}`
    )
    return toFeedPage(env)
  }

  async getAuthorPosts(authorId: string, params: { cursor?: string; limit?: number } = {}): Promise<FeedPage<IPost>> {
    const env = await auth0ApiClient.getEnvelope<PaginatedEnvelope<IPost>>(
      `${this.baseUrl}/posts/author/${authorId}${buildQuery({ ...params })}`
    )
    return toFeedPage(env)
  }

  createComment(postId: string, dto: ICreateCommentDTO): Promise<IComment> {
    return auth0ApiClient.post<IComment>(`${this.baseUrl}/posts/${postId}/comments`, dto)
  }

  deleteComment(commentId: string): Promise<void> {
    return auth0ApiClient.delete<void>(`${this.baseUrl}/comments/${commentId}`)
  }

  async getComments(postId: string, params: { cursor?: string; limit?: number } = {}): Promise<FeedPage<IComment>> {
    const env = await auth0ApiClient.getEnvelope<PaginatedEnvelope<IComment>>(
      `${this.baseUrl}/posts/${postId}/comments${buildQuery({ ...params })}`
    )
    return toFeedPage(env)
  }

  upsertReaction(postId: string, type: ReactionType): Promise<void> {
    return auth0ApiClient.put<void>(`${this.baseUrl}/posts/${postId}/reactions`, { type })
  }

  removeReaction(postId: string): Promise<void> {
    return auth0ApiClient.delete<void>(`${this.baseUrl}/posts/${postId}/reactions`)
  }

  getReactionsSummary(postId: string): Promise<Record<ReactionType, number>> {
    return auth0ApiClient.get<Record<ReactionType, number>>(`${this.baseUrl}/posts/${postId}/reactions`)
  }
}

export const feedService = new FeedService()
